import { z } from "zod";
import { MIN_WINDOW_MINUTES, windowLength } from "./windows.js";
// =============================================================================
// Validation for schedule rules (spec §11: zod on every API input).
//
// The cross-field checks matter more than the per-field ones here. A rule set
// can be individually valid in every field and still describe something
// impossible — quiet hours covering every posting window, a minimum gap wider
// than the posts-per-week implies — and the failure would only show up later as
// posts that never schedule.
// =============================================================================
const clock = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour time like 09:00 or 18:30");
/** Window length, or null if either end is malformed. Never throws — see the
 *  note in the refinement below for why that matters. */
function safeWindowLength(window) {
    try {
        return windowLength(window);
    }
    catch {
        return null;
    }
}
export const timeWindowSchema = z.object({ start: clock, end: clock });
export const scheduleRulesSchema = z
    .object({
    activeDays: z
        .array(z.number().int().min(0).max(6))
        .min(1, "Choose at least one day to post on")
        .max(7)
        .refine((days) => new Set(days).size === days.length, "Days must not repeat"),
    timeWindows: z
        .array(timeWindowSchema)
        .min(1, "Add at least one posting window")
        .max(6, "Six posting windows is plenty — more makes the schedule harder to reason about"),
    postsPerWeekMin: z.number().int().min(0).max(50),
    postsPerWeekMax: z.number().int().min(1).max(50),
    jitterMinutes: z.number().int().min(0).max(120),
    minGapHours: z.number().int().min(0).max(72),
    staggerPlatformsMinutes: z.number().int().min(0).max(180),
    skipDays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD")).max(366),
    quietHours: timeWindowSchema.nullable(),
    useBestTimes: z.boolean(),
})
    .superRefine((rules, ctx) => {
    if (rules.postsPerWeekMax < rules.postsPerWeekMin) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["postsPerWeekMax"],
            message: "The maximum must be at least the minimum",
        });
    }
    rules.timeWindows.forEach((window, i) => {
        // A field-level regex failure leaves the object "dirty", not "aborted",
        // so zod still runs this refinement with the bad value in hand. Calling
        // windowLength on "9am" would throw out of the parse and surface as a 500
        // instead of the 400 the regex already described. Skip instead: the
        // field-level issue is the accurate one.
        const length = safeWindowLength(window);
        if (length === null)
            return;
        if (length < MIN_WINDOW_MINUTES) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["timeWindows", i],
                message: `${window.start}–${window.end} is only ${length} minutes. ` +
                    `Make it at least ${MIN_WINDOW_MINUTES} so post times can vary.`,
            });
        }
    });
    // The gap has to be satisfiable inside a week, or posts silently stop
    // being placed once the first few fill the available slots.
    const activeDays = rules.activeDays.length;
    if (rules.minGapHours > 0 && rules.postsPerWeekMin > 0) {
        const maxPostsPerWeek = Math.floor((activeDays * 24) / rules.minGapHours);
        if (rules.postsPerWeekMin > maxPostsPerWeek) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["minGapHours"],
                message: `A ${rules.minGapHours}-hour gap across ${activeDays} active day(s) allows about ` +
                    `${maxPostsPerWeek} posts a week, but the minimum is ${rules.postsPerWeekMin}. ` +
                    `Lower the gap, add days, or lower the minimum.`,
            });
        }
    }
});
/** Body for saving a rule: the rules themselves, optionally scoped to a page. */
export const saveScheduleRuleSchema = z.object({
    /** Null/absent = the brand default. Set = an override for one page. */
    socialAccountId: z.string().uuid().nullish(),
    rules: scheduleRulesSchema,
});
/** Body for the settings screen's live preview. */
export const previewScheduleSchema = z.object({
    rules: scheduleRulesSchema,
    /** How many days ahead to preview. */
    days: z.number().int().min(1).max(31).default(14),
    platform: z.enum(["facebook", "instagram", "threads", "linkedin", "x"]).default("facebook"),
});
//# sourceMappingURL=schema.js.map